// Fullscreen barcode scanner used to capture HAWB / receipt numbers
// off the POD form's barcode strip. Works on iOS Safari and Android
// Chrome via getUserMedia + the @zxing/browser MultiFormat reader
// (covers Code 128, Code 39, EAN, QR, etc. — Malca and Ferrari forms
// both use Code 128 in practice).
//
// UX: tap the camera icon next to a HAWB input → this modal opens
// fullscreen → guard aims at the barcode → on first successful read,
// modal closes and calls onScan(text). Falls back to a clear error
// + Cancel button if camera permission is denied.

import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";

export default function BarcodeScanner({ onScan, onClose }) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [error, setError] = useState("");
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        // Prefer the rear-facing camera on phones — barcode is on the
        // POD paper, the guard's holding the phone over it.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        // Start continuous decoding. Auto-close on first successful read.
        const controls = await reader.decodeFromVideoDevice(
          undefined,
          videoRef.current,
          (result, err) => {
            if (cancelled) return;
            if (result) {
              const text = result.getText();
              if (text) {
                try { controlsRef.current?.stop?.(); } catch (e) { /* noop */ }
                onScan(text);
              }
            }
            // err can be NotFoundException on every frame without a
            // barcode — that's expected and noisy, so we ignore it.
          }
        );
        controlsRef.current = controls;
        setStarting(false);
      } catch (err) {
        console.error("Barcode scanner camera error", err);
        if (cancelled) return;
        const msg = err?.name === "NotAllowedError"
          ? "Camera permission denied. Allow camera access in your browser settings to scan barcodes."
          : err?.name === "NotFoundError"
            ? "No camera found on this device."
            : `Couldn't start camera: ${err?.message || err}`;
        setError(msg);
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      try { controlsRef.current?.stop?.(); } catch (e) { /* noop */ }
      // Force release of the video stream — otherwise the camera
      // indicator stays on after the modal closes.
      if (videoRef.current?.srcObject) {
        const stream = videoRef.current.srcObject;
        stream.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
      }
    };
  // Run once on mount. onScan/onClose are stable enough in practice;
  // we don't want to re-start the camera if the parent re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000",
      zIndex: 300, display: "flex", flexDirection: "column",
    }}>
      {/* Top bar with close button */}
      <div style={{
        padding: "12px 16px",
        background: "rgba(0,0,0,0.6)",
        color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          📷 Aim at the barcode
        </span>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff", borderRadius: 7,
            padding: "6px 14px", fontSize: 14, fontWeight: 700,
            cursor: "pointer",
          }}
        >Cancel</button>
      </div>

      {/* Video area */}
      <div style={{
        flex: 1, position: "relative",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden",
      }}>
        <video
          ref={videoRef}
          playsInline
          muted
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {/* Center crosshair frame — visual guide so the guard knows
            where to point. The scanner reads anywhere in frame, but
            centering speeds things up. */}
        {!error && (
          <div style={{
            position: "absolute",
            top: "50%", left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(80%, 320px)", height: "min(30%, 160px)",
            border: "3px solid #fff",
            borderRadius: 10,
            pointerEvents: "none",
            boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)",
          }} />
        )}
        {/* Starting / error overlay */}
        {(starting || error) && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, background: "rgba(0,0,0,0.7)",
          }}>
            <div style={{
              maxWidth: 320, textAlign: "center", color: "#fff", fontSize: 14,
            }}>
              {starting && !error && "Starting camera…"}
              {error && (
                <>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
                  <div style={{ marginBottom: 14 }}>{error}</div>
                  <button
                    onClick={onClose}
                    style={{
                      background: "#fff", color: "#000",
                      border: "none", borderRadius: 7,
                      padding: "8px 18px", fontSize: 14, fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >Close</button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom hint */}
      {!error && (
        <div style={{
          padding: "10px 16px",
          background: "rgba(0,0,0,0.6)",
          color: "#fff", fontSize: 11, textAlign: "center",
        }}>
          Reads automatically as soon as the barcode is sharp.
        </div>
      )}
    </div>
  );
}
